import { useState } from 'react'
import { useNavigate, useParams, Link } from 'react-router-dom'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { AxiosError } from 'axios'
import { AppShell } from '@/components/layout/AppShell'
import { primaryButtonClass } from '@/components/common/formStyles'
import { RuleEditCard } from '@/components/strategy/RuleEditCard'
import { getStrategyDetail, updateRule } from '@/lib/strategyApi'
import { useToastStore } from '@/stores/toast'
import type { UpdateRulePayload } from '@/types/strategy'

function extractDetail(err: unknown): string | undefined {
  if (err instanceof AxiosError) {
    const data = err.response?.data as { detail?: string } | undefined
    return data?.detail
  }
  return undefined
}

export function StrategyEditPage() {
  const { strategyId } = useParams<{ strategyId: string }>()
  const id = Number(strategyId)
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const pushToast = useToastStore((s) => s.pushToast)

  const [edits, setEdits] = useState<Record<number, UpdateRulePayload>>({})

  const query = useQuery({
    queryKey: ['strategy', id],
    queryFn: () => getStrategyDetail(id),
    enabled: !Number.isNaN(id),
  })

  const saveMutation = useMutation({
    mutationFn: async (pending: Record<number, UpdateRulePayload>) => {
      for (const [ruleIdStr, patch] of Object.entries(pending)) {
        await updateRule(id, Number(ruleIdStr), patch)
      }
    },
    onSuccess: () => {
      const count = Object.keys(edits).length
      queryClient.invalidateQueries({ queryKey: ['strategy', id] })
      queryClient.invalidateQueries({ queryKey: ['strategy', 'current'] })
      queryClient.invalidateQueries({ queryKey: ['strategy', 'history'] })
      setEdits({})
      pushToast({ kind: 'success', message: `${count} Regel(n) aktualisiert` })
      navigate('/strategy')
    },
    onError: (err) => {
      pushToast({
        kind: 'error',
        message: 'Speichern fehlgeschlagen',
        detail: extractDetail(err),
      })
    },
  })

  function applyEdit(ruleId: number, patch: UpdateRulePayload) {
    setEdits((prev) => {
      const next = { ...prev }
      if (Object.keys(patch).length === 0) {
        delete next[ruleId]
      } else {
        next[ruleId] = patch
      }
      return next
    })
  }

  const dirtyCount = Object.keys(edits).length
  const cancelClass =
    'tracked-caps border border-hairline px-6 py-3 text-[11px] text-ink transition-colors hover:bg-panel disabled:cursor-not-allowed disabled:opacity-50'

  return (
    <AppShell>
      <div className="mx-auto max-w-5xl">
        <h1 className="tracked-caps mb-6 text-sm text-ink">Strategie bearbeiten</h1>

        {query.isLoading && <p className="text-sm text-muted">Lade Strategie …</p>}

        {query.data && query.data.status !== 'draft' && (
          <section className="hairline border border-bernstein/40 bg-bernstein/10 p-6 text-sm text-ink">
            Diese Strategie hat Status „{query.data.status}" und ist nicht editierbar. Forke sie
            über{' '}
            <Link to="/strategy" className="text-bernstein hover:underline">
              die Strategie-Übersicht
            </Link>{' '}
            um einen neuen Draft zu erstellen.
          </section>
        )}

        {query.data?.status === 'draft' && (
          <>
            {query.data.rules.map((rule) => (
              <RuleEditCard
                key={rule.id}
                rule={rule}
                onChange={(patch) => applyEdit(rule.id, patch)}
              />
            ))}

            <footer className="mt-6 flex flex-col gap-3 md:flex-row">
              <button
                type="button"
                onClick={() => saveMutation.mutate(edits)}
                disabled={dirtyCount === 0 || saveMutation.isPending}
                className={`${primaryButtonClass} w-full md:w-auto`}
              >
                {saveMutation.isPending ? 'Speichere …' : `Speichern (${dirtyCount})`}
              </button>
              <button
                type="button"
                onClick={() => navigate('/strategy')}
                disabled={saveMutation.isPending}
                className={`${cancelClass} w-full md:w-auto`}
              >
                Abbrechen
              </button>
            </footer>
          </>
        )}
      </div>
    </AppShell>
  )
}
